import { toast } from "sonner";
import { IndianRupee, UserPlus, Mail, X, Plus, Calculator, Send, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatCurrency } from "@/lib/format";

export const EditClientModal = ({ open, onOpenChange, formData, setFormData, newAdditionalEmail, setNewAdditionalEmail, handleUpdate, submitting }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#1A1A1A] border-[#262626] w-[95vw] max-w-lg max-h-[85dvh] overflow-y-auto" aria-describedby="edit-client-description">
      <DialogHeader>
        <DialogTitle className="text-white">Edit Client</DialogTitle>
        <p id="edit-client-description" className="text-sm text-gray-500">Update client information</p>
      </DialogHeader>

      <form onSubmit={handleUpdate} className="space-y-4">
        <div>
          <Label className="text-gray-400">Name</Label>
          <Input value={formData.name || ""} onChange={(e) => setFormData({...formData, name: e.target.value})} className="bg-[#171717] border-[#262626]" />
        </div>
        <div>
          <Label className="text-gray-400">Email</Label>
          <Input type="email" value={formData.email || ""} onChange={(e) => setFormData({...formData, email: e.target.value})} className="bg-[#171717] border-[#262626]" />
        </div>
        <div>
          <Label className="text-gray-400">Phone</Label>
          <Input value={formData.phone || ""} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="bg-[#171717] border-[#262626]" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-gray-400">Approximate Sqft</Label>
            <Input type="number" value={formData.approximate_sqft || ""} onChange={(e) => setFormData({...formData, approximate_sqft: e.target.value})} className="bg-[#171717] border-[#262626]" />
          </div>
          <div>
            <Label className="text-gray-400">Rate per Sqft (₹)</Label>
            <Input type="number" value={formData.rate_per_sqft || ""} onChange={(e) => setFormData({...formData, rate_per_sqft: e.target.value})} className="bg-[#171717] border-[#262626]" />
          </div>
        </div>

        {/* Contract & Payment Tracking */}
        <div className="pt-4 border-t border-[#262626]">
          <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
            <IndianRupee className="w-4 h-4 text-[#FF4500]" /> Contract & Payment Tracking
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-400">Contract Amount (₹)</Label>
              <Input
                type="number"
                value={formData.contract_amount || ""}
                onChange={(e) => setFormData({...formData, contract_amount: e.target.value})}
                placeholder="Fixed contract amount"
                className="bg-[#171717] border-[#262626]"
              />
              <p className="text-xs text-gray-500 mt-1">The agreed final amount for this client</p>
            </div>
            <div>
              <Label className="text-gray-400">Total Received Till Date (₹)</Label>
              <Input
                type="number"
                value={formData.total_received || ""}
                onChange={(e) => setFormData({...formData, total_received: e.target.value})}
                placeholder="Amount received so far"
                className="bg-[#171717] border-[#262626]"
              />
              <p className="text-xs text-gray-500 mt-1">Update this when payment is received</p>
            </div>
            <div>
              <Label className="text-gray-400">GST Percentage</Label>
              <Input
                type="number"
                value={formData.gst_percentage || "18"}
                onChange={(e) => setFormData({...formData, gst_percentage: e.target.value})}
                className="bg-[#171717] border-[#262626]"
              />
            </div>
            {formData.contract_amount > 0 && (
              <div className="flex flex-col justify-center">
                <Label className="text-gray-400">Balance Pending</Label>
                <p className="text-xl font-bold text-[#F59E0B]">
                  {formatCurrency((parseFloat(formData.contract_amount) || 0) - (parseFloat(formData.total_received) || 0))}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Additional Contacts Section */}
        <div className="pt-4 border-t border-[#262626]">
          <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-[#FF4500]" /> Additional Contacts
          </h4>

          {/* Add new contact form */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
            <Input
              value={newAdditionalEmail.name}
              onChange={(e) => setNewAdditionalEmail({...newAdditionalEmail, name: e.target.value})}
              placeholder="Name"
              className="bg-[#171717] border-[#262626] text-sm"
            />
            <Input
              type="email"
              value={newAdditionalEmail.email}
              onChange={(e) => setNewAdditionalEmail({...newAdditionalEmail, email: e.target.value})}
              placeholder="Email *"
              className="bg-[#171717] border-[#262626] text-sm"
            />
            <div className="flex gap-1">
              <Input
                value={newAdditionalEmail.role}
                onChange={(e) => setNewAdditionalEmail({...newAdditionalEmail, role: e.target.value})}
                placeholder="Role (e.g., Spouse)"
                className="bg-[#171717] border-[#262626] text-sm flex-1"
              />
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  if (!newAdditionalEmail.email) {
                    toast.error("Please enter an email");
                    return;
                  }
                  setFormData({
                    ...formData,
                    additional_emails: [...(formData.additional_emails || []), {...newAdditionalEmail}]
                  });
                  setNewAdditionalEmail({ name: "", email: "", role: "" });
                }}
                className="bg-[#FF4500] hover:bg-[#E65100] px-2"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Existing contacts list */}
          {formData.additional_emails?.length > 0 && (
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {formData.additional_emails.map((contact, idx) => (
                <div key={idx} className="flex items-center justify-between bg-[#0f0f0f] px-3 py-2 rounded-lg text-sm">
                  <div className="flex items-center gap-3">
                    <Mail className="w-3 h-3 text-[#666]" />
                    <span className="text-white">{contact.name || 'Unnamed'}</span>
                    <span className="text-gray-500">{contact.email}</span>
                    {contact.role && <span className="text-xs text-[#FF4500]">{contact.role}</span>}
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setFormData({
                        ...formData,
                        additional_emails: formData.additional_emails.filter((_, i) => i !== idx)
                      });
                    }}
                    className="h-6 w-6 p-0"
                  >
                    <X className="w-3 h-3 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-gray-500 mt-2">Additional contacts receive project updates and notifications</p>
        </div>

        <div>
          <Label className="text-gray-400">Notes</Label>
          <Textarea value={formData.notes || ""} onChange={(e) => setFormData({...formData, notes: e.target.value})} className="bg-[#171717] border-[#262626]" />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-[#262626]">Cancel</Button>
          <Button type="submit" disabled={submitting} className="bg-[#FF4500] hover:bg-[#E65100]">{submitting ? "Saving..." : "Save Changes"}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);

export const CreateProjectModal = ({ open, onOpenChange, client, projectForm, setProjectForm, amounts, emailConfigured, emailForm, setEmailForm, handleCreateProject, submitting }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#1A1A1A] border-[#262626] max-w-2xl max-h-[90dvh] overflow-y-auto" aria-describedby="create-project-description">
      <DialogHeader>
        <DialogTitle className="text-white text-xl">Create Project</DialogTitle>
        <p id="create-project-description" className="text-sm text-gray-500">Set up project with pricing details</p>
      </DialogHeader>

      <form onSubmit={handleCreateProject} className="space-y-6">
        {/* Basic Info */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Project Details</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-400">Project Name *</Label>
              <Input value={projectForm.name} onChange={(e) => setProjectForm({...projectForm, name: e.target.value})} className="bg-[#171717] border-[#262626]" required data-testid="project-form-name" />
            </div>
            <div>
              <Label className="text-gray-400">Project Type</Label>
              <Select value={projectForm.project_type} onValueChange={(value) => setProjectForm({...projectForm, project_type: value})}>
                <SelectTrigger className="bg-[#171717] border-[#262626]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#262626]">
                  <SelectItem value="architecture">Architecture</SelectItem>
                  <SelectItem value="interior">Interior Only</SelectItem>
                  <SelectItem value="turnkey">Turnkey Project</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-gray-400">Site Location</Label>
              <Input value={projectForm.site_location} onChange={(e) => setProjectForm({...projectForm, site_location: e.target.value})} className="bg-[#171717] border-[#262626]" />
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-2">
            <Calculator className="w-4 h-4 text-[#FF4500]" />
            Pricing (Per Sqft Basis)
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-400">Approximate Sqft *</Label>
              <Input type="number" value={projectForm.approximate_sqft} onChange={(e) => setProjectForm({...projectForm, approximate_sqft: e.target.value})} placeholder="e.g., 2500" className="bg-[#171717] border-[#262626]" required data-testid="project-form-sqft" />
              <p className="text-xs text-gray-500 mt-1">Final sqft confirmed after Stage 1</p>
            </div>
            <div>
              <Label className="text-gray-400">Rate per Sqft (₹) *</Label>
              <Input type="number" value={projectForm.rate_per_sqft} onChange={(e) => setProjectForm({...projectForm, rate_per_sqft: e.target.value})} placeholder="e.g., 150" className="bg-[#171717] border-[#262626]" required data-testid="project-form-rate" />
            </div>
            <div>
              <Label className="text-gray-400">Discount Amount (₹)</Label>
              <Input type="number" value={projectForm.discount_amount} onChange={(e) => setProjectForm({...projectForm, discount_amount: e.target.value})} placeholder="0" className="bg-[#171717] border-[#262626]" />
            </div>
            <div>
              <Label className="text-gray-400">GST Percentage</Label>
              <Input type="number" value={projectForm.gst_percentage} onChange={(e) => setProjectForm({...projectForm, gst_percentage: e.target.value})} className="bg-[#171717] border-[#262626]" />
            </div>
          </div>

          {/* Calculation Summary */}
          {projectForm.approximate_sqft && projectForm.rate_per_sqft && (
            <div className="p-4 bg-[#171717] rounded border border-[#262626] space-y-2 animate-fade-in">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Quoted Amount ({projectForm.approximate_sqft} × ₹{projectForm.rate_per_sqft})</span>
                <span className="text-white">{formatCurrency(amounts.quoted)}</span>
              </div>
              {parseFloat(projectForm.discount_amount) > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Discount</span>
                  <span className="text-[#10B981]">- {formatCurrency(projectForm.discount_amount)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Final Agreed Amount</span>
                <span className="text-white font-medium">{formatCurrency(amounts.final)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">GST ({projectForm.gst_percentage}%) - Charged Extra</span>
                <span className="text-[#F59E0B]">+ {formatCurrency(amounts.gstAmount)}</span>
              </div>
              <div className="border-t border-[#262626] pt-2 mt-2 flex justify-between">
                <span className="text-white font-semibold">Total with GST</span>
                <span className="text-[#FF4500] font-bold text-lg">{formatCurrency(amounts.total)}</span>
              </div>
            </div>
          )}

          <div>
            <Label className="text-gray-400">Final Agreed Amount (₹) - Override if different</Label>
            <Input type="number" value={projectForm.final_agreed_amount} onChange={(e) => setProjectForm({...projectForm, final_agreed_amount: e.target.value})} placeholder="Leave empty to use calculated amount" className="bg-[#171717] border-[#262626]" />
          </div>
        </div>

        {/* Additional Settings */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-gray-400">Advance Amount (₹)</Label>
            <Input type="number" value={projectForm.advance_amount} onChange={(e) => setProjectForm({...projectForm, advance_amount: e.target.value})} className="bg-[#171717] border-[#262626]" />
          </div>
          <div>
            <Label className="text-gray-400">Site Visits Included</Label>
            <Input type="number" value={projectForm.site_visits_included} onChange={(e) => setProjectForm({...projectForm, site_visits_included: e.target.value})} className="bg-[#171717] border-[#262626]" />
          </div>
        </div>

        {/* Email Option */}
        {emailConfigured && (
          <div className="space-y-3 p-4 bg-[#171717] rounded border border-[#262626]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Send className="w-5 h-5 text-[#FF4500]" />
                <div>
                  <p className="text-white font-medium text-sm">Send Scope of Work Email</p>
                  <p className="text-[10px] text-gray-500">Email will be sent to {client.email}</p>
                </div>
              </div>
              <Switch
                checked={emailForm.send_email}
                onCheckedChange={(checked) => setEmailForm({...emailForm, send_email: checked})}
              />
            </div>

            {/* PDF Upload Option - shows when email is enabled */}
            {emailForm.send_email && (
              <div className="mt-3 pt-3 border-t border-[#262626] space-y-3 animate-fade-in">
                <div>
                  <Label className="text-gray-400 text-xs mb-1.5 block">Upload Scope of Work PDF (Optional)</Label>
                  <div className="relative">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setEmailForm({...emailForm, attachment: e.target.files[0]})}
                      className="hidden"
                      id="scope-pdf-upload"
                    />
                    <label
                      htmlFor="scope-pdf-upload"
                      className="flex items-center gap-2 p-3 bg-[#0D0D0D] border border-dashed border-[#333] rounded cursor-pointer hover:border-[#FF4500]/50 transition-colors"
                    >
                      <Upload className="w-4 h-4 text-gray-500" />
                      <span className="text-xs text-gray-400">
                        {emailForm.attachment ? emailForm.attachment.name : "Click to upload PDF document"}
                      </span>
                    </label>
                    {emailForm.attachment && (
                      <button
                        type="button"
                        onClick={() => setEmailForm({...emailForm, attachment: null})}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-red-400 text-xs"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <p className="text-[9px] text-gray-600 mt-1">PDF will be attached to the email</p>
                </div>

                <div>
                  <Label className="text-gray-400 text-xs">Email Subject</Label>
                  <Input
                    value={emailForm.subject}
                    onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})}
                    className="bg-[#0D0D0D] border-[#262626] h-9 text-xs mt-1"
                    placeholder={`Scope of Work - ${projectForm.name || client.project_name}`}
                  />
                </div>

                <div>
                  <Label className="text-gray-400 text-xs">Custom Message (Optional)</Label>
                  <Textarea
                    value={emailForm.custom_message}
                    onChange={(e) => setEmailForm({...emailForm, custom_message: e.target.value})}
                    placeholder="Add any additional message..."
                    className="bg-[#0D0D0D] border-[#262626] text-xs mt-1 min-h-[60px]"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-[#262626]">Cancel</Button>
          <Button type="submit" disabled={submitting} className="bg-[#FF4500] hover:bg-[#E65100] btn-glow" data-testid="submit-project-btn">
            {submitting ? "Creating..." : "Create Project"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);

export const EmailConfirmationModal = ({ open, onOpenChange, client, emailForm, setEmailForm, handleSendEmail, submitting }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#1A1A1A] border-[#262626] max-w-lg" aria-describedby="email-description">
      <DialogHeader>
        <DialogTitle className="text-white">Send Scope of Work Email</DialogTitle>
        <p id="email-description" className="text-sm text-gray-500">Review and send email to client</p>
      </DialogHeader>

      <div className="space-y-4">
        <div>
          <Label className="text-gray-400">To</Label>
          <p className="text-white">{client.name} ({client.email})</p>
        </div>
        <div>
          <Label className="text-gray-400">Subject</Label>
          <Input value={emailForm.subject} onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})} className="bg-[#171717] border-[#262626]" />
        </div>
        <div>
          <Label className="text-gray-400">Custom Message (Optional)</Label>
          <Textarea value={emailForm.custom_message} onChange={(e) => setEmailForm({...emailForm, custom_message: e.target.value})} placeholder="Add any additional message..." className="bg-[#171717] border-[#262626]" />
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-[#262626]">Skip</Button>
        <Button onClick={handleSendEmail} disabled={submitting} className="bg-[#FF4500] hover:bg-[#E65100]">
          <Send className="w-4 h-4 mr-2" />
          {submitting ? "Sending..." : "Send Email"}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
